// Apolo IDE — agent run store (session-keyed, survives navigation).
//
// Why this exists: the Code-tab agent loop runs in the renderer, driven from
// SessionWorkspace. Its state (messages, running flag, abort controller) used
// to live in component state — so leaving the screen (tab switch / session
// change) unmounted the component and ORPHANED the in-flight run: the loop
// kept going but wrote into a dead instance, and on remount loadMessages reset
// the chat to stale DB content. Symptom: "saio da tela e volto, a LLM para".
//
// Fix: keep run state HERE, keyed by session id. The loop's writes land in the
// store (durable across mounts); any mounted SessionWorkspace for that session
// subscribes and reflects it — so the run survives navigation and the view
// reconnects, showing live progress + the eventual answer. The store is a
// plain module singleton (no React) so it outlives every component.
//
// Ponte de UI: este store é a FONTE ÚNICA do "rodando" por sessão (todo run —
// local IPC ou cloud /agent-turn — passa por setRunning, e sobrevive a sair/
// voltar da tela). A sidebar pinta o dot verde lendo um espelho disso no store
// Zustand (code.streamingSessionIds), atualizado aqui no setRunning. Import é
// acíclico (useAgentStore não importa este módulo).

import { useAgentStore } from '../store/useAgentStore.js';
import { clearRunAgentMeta } from './runOutcomeBridge.js';
import { ABORT_REASON, abortWithReason } from './loop/abortReason.js';
import { createRunJournal } from './runtime/runJournal.js';

// Session Proxy (perf/RAM). This Map used to grow WITHOUT BOUND: every session
// ever opened stayed resident forever (its messages + tool payloads), so the
// renderer's RSS climbed monotonically across a long working day. Now it's an
// LRU cache — at most MAX_RESIDENT_RUNS *cold* runs are kept; the
// least-recently-used cold run is dropped when a new one is created. Eviction
// is SAFE because a cold session's history is already persisted in D1:
// reopening it re-hydrates via seedRunMessages (SessionWorkspace.loadMessages).
// Two runs are NEVER evicted — a `running` one (a live loop is writing into it)
// and a `subs.size>0` one (a mounted view is reflecting it on screen) — so the
// active session and any in-flight background run always survive; only truly
// idle, unobserved sessions are dropped.
const runs = new Map(); // sessionId -> { messages, running, controller, startedAt, subs:Set, queued:[] }
const MAX_RESIDENT_RUNS = 8;

// Move `id` to the most-recently-used end of the Map (insertion order = LRU
// order). Cheap delete+re-insert; no-op if absent or already alone.
function touch(id) {
  const r = runs.get(id);
  if (r && runs.size > 1) {
    runs.delete(id);
    runs.set(id, r);
  }
}

// Evict oldest→newest until back under the cap, skipping `keepId`, anything
// running, and anything a mounted view is subscribed to.
function evictCold(keepId) {
  if (runs.size <= MAX_RESIDENT_RUNS) return;
  for (const [id, r] of runs) {
    if (runs.size <= MAX_RESIDENT_RUNS) break;
    if (id === keepId || r.running || r.subs.size > 0) continue;
    runs.delete(id);
  }
}

function ensure(id) {
  let r = runs.get(id);
  if (!r) {
    r = { messages: [], running: false, controller: null, startedAt: null, subs: new Set(), queued: [], journal: createRunJournal() };
    runs.set(id, r);
    evictCold(id);
  }
  if (!Array.isArray(r.queued)) r.queued = []; // defensivo p/ records pré-fila
  return r;
}

function emit(r) {
  // `queued` = cópia dos payloads pendentes (não só o count) pra a UI mostrar as
  // mensagens pendentes como bolhas (estilo claude.app), não só um contador.
  const snap = { messages: r.messages, running: r.running, queuedCount: r.queued.length, queued: r.queued.slice() };
  for (const cb of r.subs) cb(snap);
}

// Read the current run state for a session. Safe before any run started.
export function getRunSnapshot(id) {
  const r = runs.get(id);
  return r
    ? { messages: r.messages, running: r.running, queuedCount: (r.queued || []).length, queued: (r.queued || []).slice() }
    : { messages: [], running: false, queuedCount: 0, queued: [] };
}

// Subscribe to updates for a session. Returns an unsubscribe fn. Subscribing
// does NOT replay — the caller reads getRunSnapshot() on mount, then this
// streams subsequent changes.
export function subscribeRun(id, cb) {
  const r = ensure(id);
  touch(id); // a view mounting = recency; keeps the active session at the MRU end
  r.subs.add(cb);
  return () => { r.subs.delete(cb); };
}

// Replace/patch the messages for a session. `updater` may be a new array or a
// (prev) => next function — mirrors the React setState signature the loop used.
export function setRunMessages(id, updater) {
  const r = ensure(id);
  r.messages = typeof updater === 'function' ? updater(r.messages) : updater || [];
  r.journal.append('message_appended', { count: r.messages.length });
  emit(r);
}

export function setRunning(id, running) {
  const r = ensure(id);
  const next = !!running;
  const changed = r.running !== next;
  r.running = next;
  // HRN3-T04 — stamp the run start in the store so the LiveActivity timer
  // survives a remount (tab switch) instead of resetting to 0 each mount.
  if (next && changed) r.startedAt = Date.now();
  if (!next) r.startedAt = null;
  if (changed) r.journal.append(next ? 'run_started' : 'run_finished', { sessionId: id });
  emit(r);
  // Espelha no store de UI pra sidebar acender o dot verde (running) AO VIVO —
  // sem depender do poll do backend (janela 48h, turn_end que nunca grava).
  // Defensivo: erro de UI nunca derruba o run.
  if (changed && id) {
    try {
      useAgentStore.getState().setSessionStreaming(id, next);
    } catch {
      /* best-effort */
    }
  }
}

export function isRunning(id) {
  return !!runs.get(id)?.running;
}

// ── Fila de envio (send queue) ────────────────────────────────────────────────
// Enquanto um turno está RODANDO, mandar uma mensagem NÃO cancela nem se perde:
// entra nesta fila (durável por sessão) e o SessionWorkspace a despacha quando o
// turno terminar (fim normal OU Stop). Assim o botão pode "deixar na fila" E o
// Stop segue funcionando — os dois convivem. Ordem FIFO.

/** Enfileira um payload de envio (o objeto que handleSend recebe). @returns {number} tamanho da fila */
export function enqueueSend(id, payload) {
  if (!id || !payload) return getQueuedCount(id);
  const r = ensure(id);
  r.queued.push(payload);
  emit(r);
  return r.queued.length;
}

/** Remove e devolve o próximo payload enfileirado (FIFO), ou null se vazio. */
export function takeNextQueued(id) {
  const r = runs.get(id);
  if (!r || !r.queued || r.queued.length === 0) return null;
  const next = r.queued.shift();
  emit(r);
  return next || null;
}

/** Quantos envios estão na fila desta sessão. */
export function getQueuedCount(id) {
  return runs.get(id)?.queued?.length || 0;
}

/** Esvazia a fila (ex.: usuário cancelou os pendentes). */
export function clearQueued(id) {
  const r = runs.get(id);
  if (!r || !r.queued || r.queued.length === 0) return;
  r.queued = [];
  emit(r);
}

/** Cancela UMA mensagem pendente pelo índice (X na bolha pendente). */
export function removeQueuedAt(id, index) {
  const r = runs.get(id);
  if (!r || !Array.isArray(r.queued) || index < 0 || index >= r.queued.length) return;
  r.queued.splice(index, 1);
  emit(r);
}

// Todos os sessionIds com run VIVA agora. Usado pelo beacon de turn-abort no
// unload (reconcilia o status "running" no servidor) e pela barra de runs em
// segundo plano (troca de perfil). Auditoria 2026-07-02.
export function listRunning() {
  const out = [];
  for (const [id, r] of runs) if (r.running) out.push(id);
  return out;
}

// Onda 4 stage-1 (anti-travamento): metadados dos runs VIVOS pro memoryGuardian
// decidir o que shedar sob pressão de RAM. `observed` = há uma view montada
// (subs>0) — o guardião NUNCA aborta um run observado, só os de fundo.
export function listRunningMeta() {
  const out = [];
  for (const [id, r] of runs) {
    if (r.running) out.push({ id, running: true, observed: r.subs.size > 0, startedAt: r.startedAt });
  }
  return out;
}

// HRN3-T04 — the run's start timestamp (ms), or null when idle. The LiveActivity
// timer reads this so elapsed time is correct across remounts.
export function getRunStartedAt(id) {
  return runs.get(id)?.startedAt || null;
}

// Seed messages from a DB load — but ONLY when idle. While a run is in flight
// (e.g. after a remount) this is a no-op so the remounted view keeps the live
// bubbles instead of being wiped back to stale persisted history.
export function seedRunMessages(id, messages) {
  const r = ensure(id);
  if (r.running) return;
  r.messages = messages || [];
  emit(r);
}

// The abort controller lives in the store so the Stop button works even after
// the component remounted (a fresh instance can still cancel the running loop).
export function setRunController(id, ctrl) {
  ensure(id).controller = ctrl;
}

export function getRunController(id) {
  return runs.get(id)?.controller || null;
}

// Drop a session's run entirely + its sibling per-session cache. Called when
// the session is deleted (CodeSidebar) so nothing lingers in memory. Idempotent.
//
// Auditoria 2026-07-02: deletar uma sessão com run VIVA só fazia runs.delete(id),
// DESCARTANDO o AbortController — o loop seguia rodando (closure), executando
// run_command/write_file no worktree, sem NENHUM caminho de Stop (a view foi
// embora com a sessão). Aborta o controller ANTES de dropar: nenhum agente órfão
// imparável. Idempotente (abort() em controller já abortado é no-op).
/** @param {string} id @param {string} [reason] */
export function evictRun(id, reason = ABORT_REASON.SESSION_DELETED) {
  const run = runs.get(id);
  if (run?.controller) {
    abortWithReason(run.controller, reason);
    run.journal?.append('run_cancelled', { reason });
  }
  runs.delete(id);
  try { clearRunAgentMeta(id); } catch { /* best-effort */ }
}

// Test-only: number of resident runs (for LRU assertions).
export function __residentRunCount() {
  return runs.size;
}

// Test-only: wipe all runs between cases.
export function __resetRuns() {
  runs.clear();
}
